import { useContext, useEffect, useState } from "react";
import { Context, UserContext } from "./Context";
import { Athlete, OwnedAthlete, RankedUser, Settings, Weekend } from "./models";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ReactGA from 'react-ga';
import { googleLogout } from '@react-oauth/google';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTP_CLIENT } from "./Util";

export const useRedirectToLoginIfNeeded = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { loggedInState } = useContext<Context>(UserContext);
    useEffect(() => {
        if (!loggedInState.loggedIn) {
            navigate({
                pathname: '/login',
                search: createSearchParams([['next', location.pathname]]).toString()
            });
        }
    }, [loggedInState.loggedIn, location.pathname, navigate])
}
export const useRedirectToHomePageIfLoggedIn = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { loggedInState } = useContext<Context>(UserContext);
    useEffect(() => {
        if (loggedInState.loggedIn) {
            navigate({
                pathname: '/'
            });
        }
    }, [loggedInState.loggedIn, location.pathname, navigate])
}
export const useDoLogin = () => {
    const navigate = useNavigate();
    const userContext = useContext<Context>(UserContext);
    const [searchParams] = useSearchParams();
    return (user: RankedUser) => {
        userContext.loginUser(user);
        navigate(searchParams.get('next') || '/');
    }
}

export const usePageTracking = () => {
    const location = useLocation();
    const [initialized, setInitialized] = useState<boolean>(false);
    const settings = useSettings().data;

    useEffect(() => {
        if (settings && settings.analytics_id) {
            ReactGA.initialize(settings.analytics_id);
            setInitialized(true);
        }
    }, [settings]);

    useEffect(() => {
        if (initialized) {
            ReactGA.pageview(window.location.pathname + window.location.search);
        }
    }, [initialized, location]);
};

export const useSignOut = (clientId: string | null) => () => {
    if (clientId) {
        googleLogout();
    }
    window.location.href = '/api/logout'
}

export const useOwnedAthletes = (selectionId: number) => {
    return useQuery<OwnedAthlete[]>({
        queryKey: ['ownedAthletes', selectionId],
        queryFn: async () => await fetchAthletes(selectionId),
        placeholderData: []
    })
}

export const useOwnedAthletesInWeekend = (weekend: Weekend) => {
    return useQuery<OwnedAthlete[]>({
        queryKey: ['ownedAthletes', 'weekend', weekend.weekend_id],
        queryFn: async () => await fetchAthletesForWeekend(weekend),
        placeholderData: []
    })
}

export const useSettings = () =>
    useQuery<Settings>({
        queryKey: ['settings'],
        queryFn: fetchSettings,
        refetchInterval: 1000 * 60 * 20
    })

export const useSetOwnedAthletes = (selectionId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (athletes: OwnedAthlete[]) => {
            const response = await HTTP_CLIENT.put<OwnedAthlete[]>(`/api/me/teams/selections/${ selectionId }/bulk`, athletes);
            return response.data;
        },
        onSuccess: (athletes) => {
            return queryClient.setQueryData(['ownedAthletes', selectionId], athletes);
        },
    });
}

export const useAddAthlete = (selectionId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (athlete: OwnedAthlete) => {
            return HTTP_CLIENT.post(`/api/me/teams/selections/${ selectionId }`, athlete);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ownedAthletes', selectionId] })
        },
    });
}

export const useRemoveAthlete = (selectionId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (athlete: Athlete) => {
            return HTTP_CLIENT.delete(`/api/me/teams/selections/${ selectionId }/${ athlete.athlete_id }`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ownedAthletes', selectionId] })
        }
    });
}

const fetchAthletes = async (selectionId: number) => {
    return await HTTP_CLIENT.get<OwnedAthlete[]>(`/api/me/teams/selections/${ selectionId }`).then(respose => respose.data);
}

const fetchAthletesForWeekend = async (weekend: Weekend): Promise<OwnedAthlete[]> => {
    const allAthletesPerSelection = await Promise.all(
            weekend.selections.map((sel) =>
                HTTP_CLIENT.get<OwnedAthlete[]>(`/api/me/teams/selections/${ sel.id }`).then(respose => respose.data)
            )
        );
    return allAthletesPerSelection.flat();
}

const fetchSettings = async () => {
    const reponse = await HTTP_CLIENT.get<Settings>("/api/settings");
    return reponse.data;
}
